import { compareAsc, format } from 'date-fns';
import { WAIT_LIST, TIMEZONE_REGEX, SEPARATOR, SOLD_OUT_SESSIONS } from '../constants';
import { RECORD_CLICK, HANDLE_CLICK } from '../actions/_types';
import { TOGGLE_MONTH, TOGGLE_MONTH_SESSIONS, TOGGLE_SOLD_OUT_SESSIONS } from '../mutations/_types';
import fixTimeZone from '../../../common/timeZone';
import groupBy from '../../../common/array';

export default ({ year }) =>
  (state, getters) =>
  ({
    [TOGGLE_MONTH]: toggleMonth,
    [TOGGLE_MONTH_SESSIONS]: toggleMonthSessions,
    [TOGGLE_SOLD_OUT_SESSIONS]: toggleSoldOutSessions,
    [RECORD_CLICK]: recordClick,
    [HANDLE_CLICK]: handleClick,
    courseName,
    sessionsByMonth,
  }) => {
    const { monthExpanded, monthSessionsExpanded, monthSoldOutSessionsExpanded } = getters;
    return Object.keys(sessionsByMonth).map((monthKey) => {
      const [month, sessionYear] = monthKey.split(' ');
      const rawSessions = sessionsByMonth[monthKey];
      const monthName = year === parseInt(sessionYear, 10) ? month : `${month} ${sessionYear}`;
      const { waitlistOnly } = rawSessions.reduce(
        ({ waitlistOnly: wOnly, totalWaitlist: totalW }, session) => {
          const isWaitlist = session.spots.status === WAIT_LIST;
          const total = isWaitlist ? totalW + 1 : totalW;

          return {
            waitlistOnly: wOnly && isWaitlist,
            totalWaitlist: total,
          };
        },
        { waitlistOnly: true, totalWaitlist: 0 },
      );

      const sortSessionsFn = (s1, s2) => compareAsc(s1.start, s2.start);

      const sessions = rawSessions
        .sort(sortSessionsFn) // Sort sessions first
        .map((session) => {
          const {
            sessionId,
            start: sessionStart,
            end: sessionEnd,
            timeZoneId,
            location: { name: location },
            uri: to,
            spots: { available, status },
          } = session;

          const isWaitlist = status === WAIT_LIST;
          const start = sessionStart.replace(TIMEZONE_REGEX, '');
          const end = sessionEnd.replace(TIMEZONE_REGEX, '');
          let startDate = `${format(start, 'dddd, MMM D')}`;
          const endDate = `${format(end, 'dddd, MMM D')}`;
          const startTime = format(start, 'h:mma');
          const endTime = format(end, 'h:mma');
          const tz = fixTimeZone(timeZoneId);
          let time = isWaitlist
            ? `${format(start, 'ddd, MMM Do: h:mma ')} ${tz}`
            : `${startTime}-${endTime} ${tz}`;

          // Handle multi-day events
          if (startDate !== endDate) {
            const startMonth = format(start, 'MMM');
            const endMonth = format(end, 'MMM');

            startDate = `${startDate}-${format(end, startMonth === endMonth ? 'D' : 'MMM D')}`;
            time = isWaitlist
              ? `${format(start, 'MMM D')}`
              : `Starts ${format(start, 'MMM D: h:mma')} ${tz}`;
          }

          return {
            sessionId,
            title: isWaitlist ? SOLD_OUT_SESSIONS : startDate,
            time,
            location,
            available,
            isWaitlist,
            link: {
              label: isWaitlist ? 'Join waitlist' : 'Sign up',
              attr: {
                href: to,
                class: [!isWaitlist && 'session-summary__cta--button'],
              },
              on: {
                click: (event) =>
                  handleClick({
                    event,
                    to,
                    external: true,
                    analytics: {
                      section: courseName,
                      linkText: `session_${sessionId}`,
                    },
                  }),
              },
            },
          };
        });

      const groupedSessions = [...groupBy(sessions, ['title', 'location'])]
        .sort(([k1, { ix: ix1 }], [k2, { ix: ix2 }]) => {
          const t1 = k1.split(SEPARATOR)[0];
          const t2 = k2.split(SEPARATOR)[0];
          if (t1 === SOLD_OUT_SESSIONS) {
            if (t2 === SOLD_OUT_SESSIONS) {
              return ix1 - ix2;
            }
            return 1;
          }
          if (t2 === SOLD_OUT_SESSIONS) {
            return -1;
          }
          return ix1 - ix2;
        })
        .map(([key, gSessions]) => [
          key,
          gSessions,
          {
            showMoreBtnToggle: (moreShown) => {
              recordClick({
                section: courseName,
                linkText: `sold-out-sessions:${monthName}_${key}_${
                  moreShown ? 'showLess' : 'showMore'
                }`,
              });
              toggleSoldOutSessions(`${monthName}_${key}`);
            },
            moreShown: monthSoldOutSessionsExpanded(`${monthName}_${key}`),
          },
        ]);

      return {
        month: monthName,
        waitlistOnly,
        sessions: groupedSessions,
        showMoreBtnToggle: (moreShown) => {
          recordClick({
            section: courseName,
            linkText: `sessions:${monthName}_${moreShown ? 'showLess' : 'showMore'}`,
          });
          toggleMonthSessions(monthName);
        },
        moreShown: monthSessionsExpanded(monthName),
        expanded: monthExpanded(monthName),
        on: {
          'accordion-toggle': () => {
            recordClick({
              section: courseName,
              linkText: `months:${monthName}_${monthExpanded(monthName) ? 'collapse' : 'expand'}`,
            });
            toggleMonth(monthName);
          },
        },
      };
    });
  };
